import type { FilterOption } from '@/components/filters/FilterDropdown';

/** Minimum width for inspector/profile filter popovers (search + close must fit). */
export const FILTER_POPOVER_MIN_WIDTH_PX = 280;

export function resolveFilterPopoverWidth(triggerWidthPx: number | undefined): number {
  const measured = triggerWidthPx && triggerWidthPx > 0 ? triggerWidthPx : 0;
  return Math.max(measured, FILTER_POPOVER_MIN_WIDTH_PX);
}

/** Value sent as `appName` for planning-v2 (matches backend `AppName.PLANNING` = `planning-v2`). */
export const PLANNING_APP_NAME = 'planning-v2' as const;

export const BOOKING_STATUS = {
  REQUESTED: {
    id: '01ae40f6-a08f-4a2b-ae42-2a18939e2387',
    name: 'Requested',
  },
  APPROVED: {
    id: '707ac1aa-119c-4615-ba12-167350e3217a',
    name: 'Approved',
  },
  REJECTED: {
    id: 'efc0acb7-9d27-4f8e-8a6b-73598fd6cf40',
    name: 'Rejected',
  },
  CANCELLED: {
    id: '6d05cbc0-ee95-43d5-8486-b6be5609f468',
    name: 'Cancelled',
  },
  COMPLETED: {
    id: '32bcfc9e-5292-44e9-95c3-d9a90a912b35',
    name: 'Completed',
  },
  LOCKED: {
    id: 'Unknown',
    name: 'Locked',
  },
} as const;

// Type that represents one of the name values from BOOKING_STATUS
export type BookingStatusText = (typeof BOOKING_STATUS)[keyof typeof BOOKING_STATUS]['name'];

// Type that represents one of the id values from BOOKING_STATUS
export type BookingStatusId = (typeof BOOKING_STATUS)[keyof typeof BOOKING_STATUS]['id'];

export const ROLES = {
  SYSTEM_ADMIN: '8EC4C6AE-A070-4A37-ACA2-9DF61B8844C8',
  CARTON_INSPECTOR: '5C02D22A-60B8-4574-BE6C-6819CF56F98F',
  INSPECTOR: '4024AD67-867B-4B78-A17D-37F9CD7229AE',
  ANALYST: 'A0189E34-57FD-4448-86B2-FFD184B9B3F5',
  PENDING: '604EA866-22A8-4D2E-A352-1FE839F87A10',
  PLANNER: '939A37A5-3FE0-460B-964B-67C29287D492',
  QC_INSPECTOR: '01AF5D40-CF37-439F-A3AE-BB30F585EC6F',
  TECHNICIAN: 'C81BA03D-9F78-43AA-B8B3-8FD262C499E2',
  REVIEWER: '84E075D2-8529-464C-B46C-42CCBC78B8F6',
  BOOKING_REQUESTOR: '40D00BD0-C67E-45C7-9CCC-00CEE0BBB9F8',
  USER_ADMIN: '496B45D8-B036-4877-BD67-6353096E0FDF',
  DECISION_EDITOR: '8026882A-E570-45F0-8F1F-4B35B41494B8',
  REGIONAL_DIRECTOR: '6F8FF70E-9377-4C54-B5DF-0B9A2B2E6E75',
  CUSTOMER: '604EA866-22A8-4D2E-A352-1FE839F87A10',
};

export const MASTER_DATA_ALLOWED_ROLE_IDS = [ROLES.SYSTEM_ADMIN] as const;

export const BOOKED_STATUS_OPTIONS: FilterOption[] = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
];

export const SYSTEM_MANAGED_BOOKING_STATUS_NAMES = ['cancelled', 'completed'] as const;

export function isSystemManagedBookingStatus(statusName: string | null | undefined): boolean {
  return SYSTEM_MANAGED_BOOKING_STATUS_NAMES.includes(
    (statusName ?? '').trim().toLowerCase() as (typeof SYSTEM_MANAGED_BOOKING_STATUS_NAMES)[number]
  );
}

export const STATUS_DROPDOWN_DISABLED_BOOKING_STATUS_NAMES = ['cancelled', 'completed'] as const;

export function isStatusDropdownDisabledForBooking(statusName: string | null | undefined): boolean {
  return STATUS_DROPDOWN_DISABLED_BOOKING_STATUS_NAMES.includes(
    (statusName ?? '')
      .trim()
      .toLowerCase() as (typeof STATUS_DROPDOWN_DISABLED_BOOKING_STATUS_NAMES)[number]
  );
}

/** Legacy DB label before QCLIVE-4000; status id is unchanged. */

/**
 * Whether a booking request is in the requested (formerly pending) status.
 * Matches by status id first, then by current or legacy display name.
 */
export function isRequestedBookingStatus(
  statusName: string | null | undefined,
  statusId?: string | null | undefined
): boolean {
  const normalizedId = String(statusId ?? '')
    .trim()
    .toLowerCase();
  if (normalizedId === BOOKING_STATUS.REQUESTED.id.toLowerCase()) {
    return true;
  }
  const normalizedName = String(statusName ?? '')
    .trim()
    .toLowerCase();
  return normalizedName === 'requested' || normalizedName === 'pending';
}

export function isRequestedBookingStatusName(statusName: string | null | undefined): boolean {
  const normalizedName = String(statusName ?? '')
    .trim()
    .toLowerCase();
  return normalizedName === 'requested' || normalizedName === 'pending';
}
